import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { User, CreditCard, History, Settings, LogOut, Play, Clock, CalendarDays, Gift, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API, LanguageToggle } from '../App';
import Navbar from '../components/Navbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { user, token, logout, updateUser } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [namedayOptions, setNamedayOptions] = useState([]);
  const [namedaySearching, setNamedaySearching] = useState(false);
  const [selectedNamedayId, setSelectedNamedayId] = useState('');
  const [manualNameday, setManualNameday] = useState(false);
  const [profileForm, setProfileForm] = useState({
    birthday_day: '',
    birthday_month: '',
    birthday_year: '',
    nameday_name: '',
    nameday_day: '',
    nameday_month: '',
    nameday_confirmed: false,
    nameday_date_rule: null,
    nameday_options: []
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        birthday_day: user.birthday_day || '',
        birthday_month: user.birthday_month || '',
        birthday_year: user.birthday_year || '',
        nameday_name: user.nameday_name || user.name?.split(' ')[0] || '',
        nameday_day: user.nameday_day || '',
        nameday_month: user.nameday_month || '',
        nameday_confirmed: !!user.nameday_confirmed,
        nameday_date_rule: user.nameday_date_rule || null,
        nameday_options: user.nameday_options || []
      });
    }
  }, [user]);

  useEffect(() => {
    const lookupName = String(profileForm.nameday_name || '').trim();
    setSelectedNamedayId('');
    setNamedayOptions([]);
    if (lookupName.length < 2) return undefined;
    const timer = setTimeout(async () => {
      setNamedaySearching(true);
      try {
        const res = await axios.get(`${API}/namedays/options?name=${encodeURIComponent(lookupName)}`);
        const options = res.data?.options || [];
        setNamedayOptions(options);
        if (options.length === 1) {
          setSelectedNamedayId(String(options[0].id));
          setProfileForm((prev) => ({
            ...prev,
            nameday_day: options[0].day,
            nameday_month: options[0].month,
            nameday_confirmed: true,
            nameday_date_rule: options[0],
            nameday_options: options
          }));
        }
      } catch (e) {
        setNamedayOptions([]);
      } finally {
        setNamedaySearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [profileForm.nameday_name]);

  const selectNamedayOption = (option) => {
    setSelectedNamedayId(String(option.id));
    setManualNameday(false);
    setProfileForm((prev) => ({
      ...prev,
      nameday_day: option.day,
      nameday_month: option.month,
      nameday_confirmed: true,
      nameday_date_rule: option,
      nameday_options: namedayOptions
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [subRes, purchRes, histRes] = await Promise.all([
          axios.get(`${API}/my-subscriptions`, { headers }),
          axios.get(`${API}/my-purchases`, { headers }),
          axios.get(`${API}/watch-history?locale=${i18n.language}`, { headers })
        ]);
        setSubscriptions(subRes.data);
        setPurchases(purchRes.data);
        setWatchHistory(histRes.data);
      } catch (e) {
        console.error('Failed to fetch profile data', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, i18n.language]);

  const handleLogout = () => {
    logout();
    toast.success(i18n.language === 'el' ? 'Αποσυνδεθήκατε' : 'Logged out');
  };

  const saveProfileDates = async () => {
    const selectedNameday = namedayOptions.find((option) => String(option.id) === String(selectedNamedayId));
    const hasMultipleNamedayOptions = namedayOptions.length > 1 && !manualNameday;
    const hasNamedayDate = selectedNameday || (profileForm.nameday_day && profileForm.nameday_month);

    if (hasMultipleNamedayOptions && !selectedNameday) {
      toast.error(i18n.language === 'el' ? 'Επιλέξτε μία ημερομηνία ονομαστικής εορτής πριν την αποθήκευση.' : 'Select one nameday date before saving.');
      return;
    }
    if (hasNamedayDate && !profileForm.nameday_confirmed) {
      toast.error(i18n.language === 'el' ? 'Επιβεβαιώστε την ονομαστική εορτή πριν την αποθήκευση.' : 'Confirm the selected nameday before saving.');
      return;
    }

    setSavingProfile(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API}/profile/dates`, {
        birthday_day: profileForm.birthday_day ? Number(profileForm.birthday_day) : null,
        birthday_month: profileForm.birthday_month ? Number(profileForm.birthday_month) : null,
        birthday_year: profileForm.birthday_year ? Number(profileForm.birthday_year) : null,
        nameday_name: profileForm.nameday_name,
        nameday_day: selectedNameday ? selectedNameday.day : (profileForm.nameday_day ? Number(profileForm.nameday_day) : null),
        nameday_month: selectedNameday ? selectedNameday.month : (profileForm.nameday_month ? Number(profileForm.nameday_month) : null),
        nameday_date_rule: selectedNameday || profileForm.nameday_date_rule || null,
        nameday_options: namedayOptions.length ? namedayOptions : (profileForm.nameday_options || []),
        nameday_confirmed: profileForm.nameday_confirmed,
        nameday_source: profileForm.nameday_confirmed ? 'user_selected' : 'unknown'
      }, { headers });
      updateUser?.(res.data?.user);
      toast.success(i18n.language === 'el' ? 'Το προφίλ ενημερώθηκε' : 'Profile updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(i18n.language === 'el' ? 'el-GR' : 'en-US');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} ${t('common.min')}`;
  };

  return (
    <div className="min-h-screen bg-misel-bg">
      <Navbar />

      <div className="pt-24 px-8 md:px-16 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Profile Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <User size={32} className="text-primary" />
              </div>
              <div>
                <h1 className="font-roboto font-bold text-2xl text-white">{user?.name}</h1>
                <p className="text-gray-400">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LanguageToggle />
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut size={18} className="mr-2" />
                {t('nav.logout')}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="subscriptions" className="w-full">
            <TabsList className="bg-misel-card border border-white/10 mb-8">
              <TabsTrigger value="subscriptions" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <CreditCard size={16} className="mr-2" />
                {t('profile.subscriptions')}
              </TabsTrigger>
              <TabsTrigger value="purchases" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <CreditCard size={16} className="mr-2" />
                {t('profile.purchases')}
              </TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <History size={16} className="mr-2" />
                {t('profile.watch_history')}
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Settings size={16} className="mr-2" />
                {i18n.language === 'el' ? 'Προφίλ' : 'Profile'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="subscriptions">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-24 bg-misel-card animate-pulse rounded-sm" />
                  ))}
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="text-center py-12 bg-misel-card rounded-sm border border-white/10">
                  <CreditCard size={48} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">
                    {i18n.language === 'el' ? 'Δεν έχετε ενεργές συνδρομές' : 'No active subscriptions'}
                  </p>
                  <Link to="/pricing">
                    <Button className="bg-primary hover:bg-primary/90">
                      {t('pricing.subscribe')}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {subscriptions.map((sub) => (
                    <div key={sub.id} className="bg-misel-card rounded-sm border border-white/10 p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-white font-medium mb-1">
                            {sub.scope === 'global' 
                              ? (i18n.language === 'el' ? 'Πλήρης Πρόσβαση' : 'Full Access')
                              : sub.plan_id
                            }
                          </h3>
                          <p className="text-gray-500 text-sm">
                            {i18n.language === 'el' ? 'Ενεργή έως' : 'Active until'}: {formatDate(sub.current_period_end)}
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-sm text-sm">
                          {i18n.language === 'el' ? 'Ενεργή' : 'Active'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="purchases">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-24 bg-misel-card animate-pulse rounded-sm" />
                  ))}
                </div>
              ) : purchases.length === 0 ? (
                <div className="text-center py-12 bg-misel-card rounded-sm border border-white/10">
                  <CreditCard size={48} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">
                    {i18n.language === 'el' ? 'Δεν έχετε αγορές' : 'No purchases yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {purchases.map((purchase) => (
                    <div key={purchase.id} className="bg-misel-card rounded-sm border border-white/10 p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-white font-medium mb-1">
                            Video ID: {purchase.video_id}
                          </h3>
                          <p className="text-gray-500 text-sm">
                            €{purchase.amount} • {formatDate(purchase.created_at)}
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-sm text-sm">
                          {i18n.language === 'el' ? 'Ολοκληρώθηκε' : 'Completed'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="history">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="aspect-video bg-misel-card animate-pulse rounded-sm" />
                  ))}
                </div>
              ) : watchHistory.length === 0 ? (
                <div className="text-center py-12 bg-misel-card rounded-sm border border-white/10">
                  <History size={48} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">
                    {i18n.language === 'el' ? 'Δεν έχετε ιστορικό παρακολούθησης' : 'No watch history'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {watchHistory.map((item) => {
                    const hasVideoSlug = Boolean(item.slug);
                    const card = (
                      <>
                      <div className="aspect-video relative overflow-hidden rounded-sm bg-misel-card border border-white/5">
                        <div 
                          className="absolute inset-0 bg-cover bg-center"
                          style={{ 
                            backgroundImage: item.thumbnail_url 
                              ? `url('${item.thumbnail_url}')`
                              : `url('https://images.pexels.com/photos/15659458/pexels-photo-15659458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
                          }}
                        />
                        <div className="absolute inset-0 bg-black/50" />
                        
                        {/* Play overlay */}
                        {hasVideoSlug && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center">
                              <Play size={20} className="text-white ml-0.5" fill="white" />
                            </div>
                          </div>
                        )}

                        {/* Progress bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                          <div 
                            className="h-full bg-primary"
                            style={{ width: `${(item.progress_seconds / item.duration_seconds) * 100}%` }}
                          />
                        </div>

                        {/* Duration badge */}
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/80 rounded text-xs text-white flex items-center gap-1">
                          <Clock size={10} />
                          {formatDuration(item.duration_seconds)}
                        </div>
                      </div>
                      <div className="mt-2">
                        <h4 className={`text-white text-sm font-medium line-clamp-1 transition-colors ${hasVideoSlug ? 'group-hover:text-primary' : ''}`}>
                          {item.title}
                        </h4>
                        <p className="text-gray-500 text-xs mt-1">
                          {item.completed 
                            ? (i18n.language === 'el' ? 'Ολοκληρώθηκε' : 'Completed')
                            : `${Math.floor((item.progress_seconds / item.duration_seconds) * 100)}% ${i18n.language === 'el' ? 'παρακολουθήθηκε' : 'watched'}`
                          }
                        </p>
                      </div>
                      </>
                    );

                    return hasVideoSlug ? (
                      <Link key={item.video_id} to={`/video/${item.slug}`} className="group">
                        {card}
                      </Link>
                    ) : (
                      <div key={item.video_id} className="group cursor-default">
                        {card}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="settings">
              <div className="bg-misel-card rounded-sm border border-white/10 p-6 space-y-6">
                <div>
                  <h2 className="text-white text-xl font-bold mb-2 flex items-center gap-2">
                    <CalendarDays size={20} className="text-primary" />
                    {i18n.language === 'el' ? 'Γενέθλια' : 'Birthday'}
                  </h2>
                  <p className="text-gray-500 text-sm mb-4">
                    {i18n.language === 'el'
                      ? 'Θέλουμε να είμαστε μαζί σου στις σημαντικές σου στιγμές.'
                      : "We'd like to be with you on your important moments."}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input type="number" min="1" max="31" value={profileForm.birthday_day} onChange={(e) => setProfileForm({ ...profileForm, birthday_day: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder={i18n.language === 'el' ? 'Ημέρα' : 'Day'} />
                    <Input type="number" min="1" max="12" value={profileForm.birthday_month} onChange={(e) => setProfileForm({ ...profileForm, birthday_month: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder={i18n.language === 'el' ? 'Μήνας' : 'Month'} />
                    <Input type="number" min="1900" max="2100" value={profileForm.birthday_year} onChange={(e) => setProfileForm({ ...profileForm, birthday_year: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder={i18n.language === 'el' ? 'Έτος προαιρετικό' : 'Year optional'} />
                  </div>
                </div>

                <div>
                  <h2 className="text-white text-xl font-bold mb-2 flex items-center gap-2">
                    <Gift size={20} className="text-primary" />
                    {i18n.language === 'el' ? 'Ονομαστική εορτή' : 'Nameday'}
                  </h2>
                  <p className="text-gray-500 text-sm mb-4">
                    {i18n.language === 'el'
                      ? 'Επιβεβαίωσε την ακριβή ημερομηνία για να λειτουργούν σωστά οι αυτοματισμοί μηνυμάτων.'
                      : 'Confirm the exact date so messaging automations can work correctly.'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input value={profileForm.nameday_name} onChange={(e) => setProfileForm({ ...profileForm, nameday_name: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder={i18n.language === 'el' ? 'Όνομα γιορτής' : 'Nameday name'} />
                  </div>
                  {namedaySearching && <p className="text-sm text-gray-400 mt-3">Αναζήτηση ονομαστικής εορτής...</p>}
                  {namedayOptions.length > 1 && <p className="text-sm text-gray-300 mt-3">Για το όνομά σου βρήκαμε περισσότερες από μία πιθανές ημερομηνίες. Ποια είναι η σωστή;</p>}
                  {namedayOptions.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {namedayOptions.map((option) => (
                        <label key={`${option.id}-${option.date}`} className="flex items-center gap-3 rounded-sm border border-white/10 bg-black/20 p-3 text-sm text-gray-200">
                          <input type="radio" name="profile-nameday-option" checked={String(selectedNamedayId) === String(option.id)} onChange={() => selectNamedayOption(option)} />
                          <span>{new Date(option.date).toLocaleDateString('el-GR', { day: 'numeric', month: 'long' })} - {option.label_el || option.canonical_name}</span>
                        </label>
                      ))}
                      <button type="button" className="text-sm text-primary hover:underline" onClick={() => { setManualNameday(true); setSelectedNamedayId(''); setProfileForm({ ...profileForm, nameday_confirmed: false }); }}>
                        Δεν είναι καμία από αυτές
                      </button>
                    </div>
                  )}
                  {(manualNameday || (!namedaySearching && profileForm.nameday_name && namedayOptions.length === 0)) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <Input type="number" min="1" max="31" value={profileForm.nameday_day} onChange={(e) => setProfileForm({ ...profileForm, nameday_day: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Ημέρα" />
                      <Input type="number" min="1" max="12" value={profileForm.nameday_month} onChange={(e) => setProfileForm({ ...profileForm, nameday_month: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Μήνας" />
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-300 mt-4">
                    <input type="checkbox" checked={profileForm.nameday_confirmed} onChange={(e) => setProfileForm({ ...profileForm, nameday_confirmed: e.target.checked })} />
                    {i18n.language === 'el' ? 'Έχω επιβεβαιώσει ότι αυτή είναι η σωστή ημερομηνία' : 'I confirm this is the correct date'}
                  </label>
                </div>

                <Button onClick={saveProfileDates} disabled={savingProfile} className="bg-primary hover:bg-primary/90">
                  <Save size={16} className="mr-2" />
                  {savingProfile ? (i18n.language === 'el' ? 'Αποθήκευση...' : 'Saving...') : (i18n.language === 'el' ? 'Αποθήκευση προφίλ' : 'Save profile')}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
